import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { CodeIcon, MobileAltIcon, DesktopIcon, DatabaseIcon, CloudIcon, LockIcon, ToolsIcon, RobotIcon, ProjectDiagramIcon } from './IconComponents';
import { useTranslation, Trans } from 'react-i18next';

const ServicesSection = styled.section`
  padding: 5rem 0;
  background-color: var(--light-background);
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  color: var(--text-color);
  font-size: 1.1rem;
`;

const ServicesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const ServiceCard = styled.div`
  background-color: var(--background-color);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: var(--transition);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const ClickableServiceCard = styled(Link)`
  background-color: var(--background-color);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: var(--transition);
  text-decoration: none;
  color: inherit;
  display: block;
  cursor: pointer;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    text-decoration: none;
    color: inherit;
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: var(--light-text-color);
  font-size: 1.8rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ServiceDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const Services: React.FC = () => {
  const { t, i18n } = useTranslation();

  const services = [
    {
      id: 1,
      icon: <CodeIcon />,
      title: t('services.items.0.title'),
      description: t('services.items.0.description')
    },
    {
      id: 2,
      icon: <MobileAltIcon />,
      title: t('services.items.1.title'),
      description: t('services.items.1.description')
    },
    {
      id: 3,
      icon: <DesktopIcon />,
      title: t('services.items.2.title'),
      description: t('services.items.2.description')
    },
    {
      id: 4,
      icon: <DatabaseIcon />,
      title: t('services.items.3.title'),
      description: t('services.items.3.description')
    },
    {
      id: 5,
      icon: <CloudIcon />,
      title: t('services.items.4.title'),
      description: t('services.items.4.description')
    },
    {
      id: 6,
      icon: <LockIcon />,
      title: t('services.items.5.title'),
      description: t('services.items.5.description')
    },
    {
      id: 7,
      icon: <ToolsIcon />,
      title: t('services.items.6.title'),
      description: t('services.items.6.description')
    },
    {
      id: 8,
      icon: <RobotIcon />,
      title: t('services.items.7.title'),
      description: t('services.items.7.description')
    },
    {
      id: 9,
      icon: <ProjectDiagramIcon />,
      title: t('services.items.8.title'),
      description: t('services.items.8.description')
    }
  ];

  return (
    <ServicesSection id="services">
      <SectionTitle>
        <Trans i18nKey="services.title">
          Our <span>Services</span>
        </Trans>
      </SectionTitle>
      <SectionSubtitle>
        {t('services.subtitle')}
      </SectionSubtitle>
      <ServicesContainer>
        {services.map((service) => {
          // Make Individuelle Softwareentwicklung service clickable
          if (service.id === 1) {
            return (
              <ClickableServiceCard key={service.id} to="/individuelle-softwareentwicklung">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }
          
          // Make App-Entwicklung service clickable
          if (service.id === 2) {
            return (
              <ClickableServiceCard key={service.id} to="/app-entwicklung">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }

          // Make Desktop-Anwendungen service clickable
          if (service.id === 3) {
            return (
              <ClickableServiceCard key={service.id} to="/desktop-anwendungen">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }

          // Make Prozessoptimierung service clickable
          if (service.id === 4) {
            return (
              <ClickableServiceCard key={service.id} to="/prozessoptimierung">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }

          // Make Dynamics 365 service clickable
          if (service.id === 5) {
            return (
              <ClickableServiceCard key={service.id} to="/dynamics365">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }
          
          // Make Automatisierung service clickable
          if (service.id === 6) {
            return (
              <ClickableServiceCard key={service.id} to="/automatisierung">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }
          
          // Make DevOps service clickable
          if (service.id === 7) {
            return (
              <ClickableServiceCard key={service.id} to="/devops">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }

          // Make KI service clickable
          if (service.id === 8) {
            return (
              <ClickableServiceCard key={service.id} to="/ki">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }

          // Make Projektmanagement service clickable
          if (service.id === 9) {
            return (
              <ClickableServiceCard key={service.id} to="/projektmanagement">
                <IconWrapper>{service.icon}</IconWrapper>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ClickableServiceCard>
            );
          }
          
          return (
            <ServiceCard key={service.id}>
              <IconWrapper>{service.icon}</IconWrapper>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
            </ServiceCard>
          );
        })}
      </ServicesContainer>
    </ServicesSection>
  );
};

export default Services;
